<script>
import { useElixioConnectStore } from '../../stores/elixioConnect'
import EcButton from '../ui/EcButton.vue'
import EcErrorBanner from '../ui/EcErrorBanner.vue'
import {
  PACKAGING_TYPES,
  WEIGHT_UNITS,
} from '../../types/shipment'

export default {
  name: 'ShipmentForm',
  components: { EcButton, EcErrorBanner },
  props: {
    shippableType: { type: String, required: true },
    shippableId: { type: [String, Number], required: true },
    initialData: { type: Object, default: null },
  },
  data() {
    return {
      store: useElixioConnectStore(),
      submitting: false,
      errors: [],
      PACKAGING_TYPES,
      WEIGHT_UNITS,
      PAYMENT_TYPES: ['Sender', 'ThirdParty'],
      form: {
        name: '',
        packaging_type: 'package',
        payment_type: 'Sender',
        sender: {
          company_name: '',
          contact_name: '',
          email_address: '',
          residential: false,
          address: {
            street_address: '',
            city: '',
            region: '',
            country: 'CA',
            postal_code: '',
            phone_code: '+1',
            phone_number: '',
          },
        },
        recipient: {
          company_name: '',
          attention_to: '',
          residential: false,
          address: {
            street_address: '',
            city: '',
            region: '',
            country: 'CA',
            postal_code: '',
            phone_code: '+1',
            phone_number: '',
          },
        },
        packaging_properties: {
          weight: { unit: 'lb', value: '' },
          packages: [{ length: '', width: '', depth: '', weight: '', quantity: '1' }],
        },
        additional_options: {
          declared_value: '',
        },
      },
    }
  },
  computed: {
    t() {
      return this.$ecTranslate || ((k) => k)
    },
    notify() {
      return this.$ecNotify || (() => {})
    },
    isEditing() {
      return !!this.initialData
    },
  },
  mounted() {
    if (this.initialData) {
      this.populateForm(this.initialData)
    }
  },
  methods: {
    addPackage() {
      this.form.packaging_properties.packages.push({
        length: '',
        width: '',
        depth: '',
        weight: '',
        quantity: '1',
      })
    },
    removePackage(index) {
      this.form.packaging_properties.packages.splice(index, 1)
    },
    populateForm(data) {
      if (!data) return
      this.form.name = data.name ?? ''
      this.form.packaging_type = data.packaging_type ?? 'package'
      this.form.payment_type = data.payment_type ?? 'Sender'

      if (data.sender) {
        Object.assign(this.form.sender, data.sender)
        if (data.sender.address) {
          Object.assign(this.form.sender.address, data.sender.address)
        }
      }
      if (data.recipient) {
        Object.assign(this.form.recipient, data.recipient)
        if (data.recipient.address) {
          Object.assign(this.form.recipient.address, data.recipient.address)
        }
      }
      if (data.packaging_properties) {
        if (data.packaging_properties.weight) {
          Object.assign(this.form.packaging_properties.weight, data.packaging_properties.weight)
        }
        if (data.packaging_properties.packages?.length) {
          this.form.packaging_properties.packages = [...data.packaging_properties.packages]
        }
      }
      if (data.additional_options) {
        Object.assign(this.form.additional_options, data.additional_options)
        if (this.form.additional_options.declared_value == null) {
          this.form.additional_options.declared_value = ''
        }
      }
    },
    parseBodyMessage(body) {
      if (!body || typeof body !== 'string') return null
      try {
        const parsed = JSON.parse(body)
        if (parsed.message) return parsed.message
      } catch { /* not valid JSON */ }
      return null
    },
    cleanErrorString(err) {
      const match = err.match(/"message"\s*:\s*"([^"]+)"/)
      return match ? match[1] : null
    },
    extractErrors(error) {
      const data = error?.response?.data
      if (!data) return [this.t('ELIXIO_CONNECT.SOMETHING_WENT_WRONG')]

      const msgs = []
      const deepest = data?.response?.response

      if (deepest?.body) {
        const bodyMsg = this.parseBodyMessage(deepest.body)
        if (bodyMsg) msgs.push(bodyMsg)
      }

      if (!msgs.length && deepest?.error && typeof deepest.error === 'string') {
        const cleaned = this.cleanErrorString(deepest.error)
        if (cleaned) msgs.push(cleaned)
      }

      if (!msgs.length) {
        const validationErrors = deepest?.error ?? data?.response?.error ?? data?.error
        if (validationErrors && typeof validationErrors === 'object' && !Array.isArray(validationErrors)) {
          for (const field of Object.keys(validationErrors)) {
            const fieldErrors = validationErrors[field]
            if (Array.isArray(fieldErrors)) {
              msgs.push(...fieldErrors)
            } else {
              msgs.push(String(fieldErrors))
            }
          }
        }
      }

      if (!msgs.length) {
        const apiErrors = deepest?.errors ?? data?.response?.errors ?? data?.errors
        if (Array.isArray(apiErrors) && apiErrors.length) {
          msgs.push(...apiErrors.map((e) => e.detail ?? e.title ?? String(e)))
        }
      }

      if (!msgs.length && deepest?.message) {
        msgs.push(deepest.message)
      }
      if (!msgs.length && data?.response?.detail) {
        msgs.push(data.response.detail)
      }
      if (!msgs.length && data?.response?.title) {
        msgs.push(data.response.title)
      }
      if (!msgs.length && data?.response?.message) {
        msgs.push(data.response.message)
      }
      if (!msgs.length && data?.message) {
        msgs.push(data.message)
      }

      return msgs.length ? msgs : [this.t('ELIXIO_CONNECT.SOMETHING_WENT_WRONG')]
    },
    async submit() {
      this.errors = []
      this.submitting = true

      if (this.form.additional_options.declared_value == null) {
        this.form.additional_options.declared_value = ''
      }

      const totalWeight = this.form.packaging_properties.packages.reduce((sum, pkg) => {
        const w = parseFloat(pkg.weight) || 0
        const q = parseInt(pkg.quantity) || 1
        return sum + w * q
      }, 0)
      this.form.packaging_properties.weight.value = totalWeight > 0 ? String(totalWeight) : ''

      try {
        if (this.isEditing) {
          await this.store.editEstimation(this.initialData.id, this.form)
          this.notify(this.t('ELIXIO_CONNECT.ESTIMATION_UPDATED'), 'success')
        } else {
          await this.store.estimateShipment(this.shippableType, this.shippableId, this.form)
          this.notify(this.t('ELIXIO_CONNECT.ESTIMATION_CREATED'), 'success')
        }
        this.$emit('saved')
      } catch (e) {
        this.errors = this.extractErrors(e)
        this.notify(this.errors[0] ?? this.t('ELIXIO_CONNECT.SOMETHING_WENT_WRONG'), 'error')
        this.$nextTick(() => {
          if (this.$refs.formEl) {
            this.$refs.formEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<template>
  <form ref="formEl" class="ec-shipment-form" @submit.prevent="submit">
    <EcErrorBanner v-if="errors.length" :errors="errors" class="ec-mb-3" />

    <!-- General info -->
    <fieldset class="ec-fieldset">
      <legend class="ec-fieldset__legend">
        <i class="bi bi-box-seam" />
        {{ t('ELIXIO_CONNECT.GENERAL_INFORMATION') }}
      </legend>

      <div class="ec-form-group">
        <label class="ec-form-label">{{ t('ELIXIO_CONNECT.SHIPMENT_NAME') }}</label>
        <input v-model="form.name" type="text" class="ec-input__field" :placeholder="t('ELIXIO_CONNECT.SHIPMENT_NAME')" />
      </div>

      <div class="ec-form-row ec-form-row--3">
        <div class="ec-form-group">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.PACKAGING_TYPE') }}</label>
          <select v-model="form.packaging_type" class="ec-select__field">
            <option v-for="pt in PACKAGING_TYPES" :key="pt" :value="pt">
              {{ t('ELIXIO_CONNECT.' + pt) }}
            </option>
          </select>
        </div>
        <div class="ec-form-group">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.WEIGHT_UNIT') }}</label>
          <select v-model="form.packaging_properties.weight.unit" class="ec-select__field">
            <option v-for="wu in WEIGHT_UNITS" :key="wu" :value="wu">
              {{ t('ELIXIO_CONNECT.' + wu) }}
            </option>
          </select>
        </div>
        <div class="ec-form-group">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.PAYMENT_TYPE') }}</label>
          <select v-model="form.payment_type" class="ec-select__field">
            <option v-for="pt in PAYMENT_TYPES" :key="pt" :value="pt">
              {{ t('ELIXIO_CONNECT.' + pt) }}
            </option>
          </select>
        </div>
      </div>
    </fieldset>

    <!-- Sender / Recipient side by side -->
    <div class="ec-shipment-form__parties">
      <!-- Sender -->
      <fieldset class="ec-fieldset">
        <legend class="ec-fieldset__legend">
          <i class="bi bi-building" />
          {{ t('ELIXIO_CONNECT.SENDER') }}
        </legend>
        <div class="ec-form-group">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.COMPANY_NAME') }}</label>
          <input v-model="form.sender.company_name" type="text" class="ec-input__field" />
        </div>
        <div class="ec-form-group">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.CONTACT_NAME') }}</label>
          <input v-model="form.sender.contact_name" type="text" class="ec-input__field" />
        </div>
        <div class="ec-form-group">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.EMAIL') }}</label>
          <input v-model="form.sender.email_address" type="email" class="ec-input__field" />
        </div>
        <div class="ec-form-group">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.PHONE') }}</label>
          <input v-model="form.sender.address.phone_number" type="text" class="ec-input__field" />
        </div>
        <div class="ec-form-group">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.ADDRESS') }}</label>
          <input v-model="form.sender.address.street_address" type="text" class="ec-input__field" />
        </div>
        <div class="ec-form-row">
          <div class="ec-form-group">
            <label class="ec-form-label">{{ t('ELIXIO_CONNECT.CITY') }}</label>
            <input v-model="form.sender.address.city" type="text" class="ec-input__field" />
          </div>
          <div class="ec-form-group">
            <label class="ec-form-label">{{ t('ELIXIO_CONNECT.PROVINCE') }}</label>
            <input v-model="form.sender.address.region" type="text" class="ec-input__field" />
          </div>
        </div>
        <div class="ec-form-group">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.POSTAL_CODE') }}</label>
          <input v-model="form.sender.address.postal_code" type="text" class="ec-input__field" />
        </div>
      </fieldset>

      <!-- Recipient -->
      <fieldset class="ec-fieldset">
        <legend class="ec-fieldset__legend">
          <i class="bi bi-geo-alt" />
          {{ t('ELIXIO_CONNECT.RECIPIENT') }}
        </legend>
        <div class="ec-form-group">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.COMPANY_NAME') }}</label>
          <input v-model="form.recipient.company_name" type="text" class="ec-input__field" />
        </div>
        <div class="ec-form-group">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.ATTENTION_TO') }}</label>
          <input v-model="form.recipient.attention_to" type="text" class="ec-input__field" />
        </div>
        <div class="ec-form-group">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.PHONE') }}</label>
          <input v-model="form.recipient.address.phone_number" type="text" class="ec-input__field" />
        </div>
        <div class="ec-form-group">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.ADDRESS') }}</label>
          <input v-model="form.recipient.address.street_address" type="text" class="ec-input__field" />
        </div>
        <div class="ec-form-row">
          <div class="ec-form-group">
            <label class="ec-form-label">{{ t('ELIXIO_CONNECT.CITY') }}</label>
            <input v-model="form.recipient.address.city" type="text" class="ec-input__field" />
          </div>
          <div class="ec-form-group">
            <label class="ec-form-label">{{ t('ELIXIO_CONNECT.PROVINCE') }}</label>
            <input v-model="form.recipient.address.region" type="text" class="ec-input__field" />
          </div>
        </div>
        <div class="ec-form-group">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.POSTAL_CODE') }}</label>
          <input v-model="form.recipient.address.postal_code" type="text" class="ec-input__field" />
        </div>
      </fieldset>
    </div>

    <!-- Packages -->
    <fieldset class="ec-fieldset">
      <legend class="ec-fieldset__legend">
        <i class="bi bi-boxes" />
        {{ t('ELIXIO_CONNECT.PACKAGE_DETAILS') }}
      </legend>

      <div class="ec-form-group">
        <label class="ec-form-label">{{ t('ELIXIO_CONNECT.DECLARED_VALUE') }}</label>
        <input v-model="form.additional_options.declared_value" type="text" class="ec-input__field" style="max-width: 200px" />
      </div>

      <div
        v-for="(pkg, index) in form.packaging_properties.packages"
        :key="index"
        class="ec-shipment-form__package"
      >
        <div class="ec-shipment-form__package-header">
          <span class="ec-fw-medium">{{ t('ELIXIO_CONNECT.PACKAGE') }} #{{ index + 1 }}</span>
          <EcButton
            v-if="form.packaging_properties.packages.length > 1"
            variant="outline-danger"
            size="sm"
            icon="trash"
            @click="removePackage(index)"
          />
        </div>

        <div class="ec-form-row ec-form-row--4">
          <div class="ec-form-group">
            <label class="ec-form-label">{{ t('ELIXIO_CONNECT.LENGTH') }} (in)</label>
            <input v-model="pkg.length" type="text" class="ec-input__field" />
          </div>
          <div class="ec-form-group">
            <label class="ec-form-label">{{ t('ELIXIO_CONNECT.WIDTH') }} (in)</label>
            <input v-model="pkg.width" type="text" class="ec-input__field" />
          </div>
          <div class="ec-form-group">
            <label class="ec-form-label">{{ t('ELIXIO_CONNECT.HEIGHT') }} (in)</label>
            <input v-model="pkg.depth" type="text" class="ec-input__field" />
          </div>
          <div class="ec-form-group">
            <label class="ec-form-label">{{ t('ELIXIO_CONNECT.WEIGHT') }} ({{ form.packaging_properties.weight.unit }})</label>
            <input v-model="pkg.weight" type="text" class="ec-input__field" />
          </div>
        </div>
        <div class="ec-form-group" style="max-width: 120px">
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.QUANTITY') }}</label>
          <input v-model="pkg.quantity" type="text" class="ec-input__field" />
        </div>
      </div>

      <EcButton variant="outline-success" size="sm" icon="plus-lg" @click="addPackage">
        {{ t('ELIXIO_CONNECT.ADD_PACKAGE') }}
      </EcButton>
    </fieldset>

    <!-- Actions -->
    <div class="ec-flex ec-justify-end ec-gap-2 ec-mt-3">
      <EcButton variant="outline-secondary" @click="$emit('cancel')">
        {{ t('ELIXIO_CONNECT.CANCEL') }}
      </EcButton>
      <EcButton variant="primary" :loading="submitting" @click="submit">
        {{ isEditing ? t('ELIXIO_CONNECT.REDO_ESTIMATION') : t('ELIXIO_CONNECT.ESTIMATE_SHIPMENT') }}
      </EcButton>
    </div>
  </form>
</template>

<style scoped>
.ec-shipment-form__parties {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

@media (max-width: 768px) {
  .ec-shipment-form__parties {
    grid-template-columns: 1fr;
  }
}

.ec-shipment-form__package {
  background: var(--ec-bg-muted, #f8f9fa);
  border: 1px solid var(--ec-border-color, #e2e8f0);
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
}

.ec-shipment-form__package-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--ec-border-color, #e2e8f0);
}
</style>
